import React, { useLayoutEffect, useRef } from 'react';
import { Box } from '@mui/material';

import { ErrorBoundary } from 'react-error-boundary';
import {
  Panel,
  PanelGroup,
  type ImperativePanelHandle,
} from 'react-resizable-panels';

import { ResizeHandleWithTooltip } from '@watershed/shared-frontend/components/ResizeHandleWithTooltip';

import { AIChatSidebarTrigger } from '../components/ai/components/AIChatSidebarTrigger';
import { useCoPilot } from '../components/ai-service/CoPilotContext';
import { ErrorFallback } from '../components/ErrorFallback';
import SidebarLayout from './SidebarLayout';

export default function CoPilotLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { activeCoPilot, isCoPilotOpen, toggleCoPilot, closeCoPilot } =
    useCoPilot();
  const copilotPanelRef = useRef<ImperativePanelHandle>(null);

  // Sync panel collapse/expand with isCoPilotOpen state.
  // useLayoutEffect avoids a visual flash before the panel collapses.
  useLayoutEffect(() => {
    const panel = copilotPanelRef.current;
    if (!panel) return;
    if (isCoPilotOpen) {
      panel.expand();
    } else {
      panel.collapse();
    }
  }, [isCoPilotOpen]);

  // If no CoPilot is active, just use the regular SidebarLayout
  if (!activeCoPilot) {
    return <SidebarLayout>{children}</SidebarLayout>;
  }

  // When CoPilot is active, wrap the children in resizable panels.
  // The copilot Panel is always rendered to preserve chat state across
  // close/open — it collapses to 0 size when closed via the imperative API.
  // Note: Panel needs display:flex and flexDirection:column to properly
  // contain pages that use flex: 1 for their height
  const coPilotContent = (
    <PanelGroup direction="horizontal" id="copilot-panels">
      <Panel
        id="main-content"
        order={1}
        defaultSize={75}
        minSize={50}
        style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        {children}
      </Panel>

      {/* Only render resize handle when CoPilot panel is expanded */}
      {isCoPilotOpen && (
        <ResizeHandleWithTooltip
          id="copilot-resize-handle"
          direction="horizontal"
        />
      )}

      {/* Always render the CoPilot panel so the component stays mounted
          and chat state is preserved when the panel is closed. */}
      <Panel
        ref={copilotPanelRef}
        id="copilot-sidebar"
        order={2}
        defaultSize={25}
        minSize={10}
        maxSize={40}
        collapsible
        collapsedSize={0}
        onCollapse={closeCoPilot}
      >
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              height: '100dvh',
              borderLeft: (theme) => `0.5px solid ${theme.palette.grey30}`,
              backgroundColor: (theme) => theme.palette.grey05,
              // Establish a stacking context above whatever content sits
              // under this sidebar. The motivation for this is that the flexible
              // report document editor has floating buttons with a high z-index
              // which were displaying on top of the AI copilot content.
              position: 'relative',
              zIndex: 1,
            }}
          >
            <activeCoPilot.component onClose={closeCoPilot} />
          </Box>
        </ErrorBoundary>
      </Panel>
    </PanelGroup>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <SidebarLayout>{coPilotContent}</SidebarLayout>
      {/* Only show the trigger when CoPilot is active and not open */}
      {!isCoPilotOpen && (
        <AIChatSidebarTrigger isOpen={isCoPilotOpen} onClick={toggleCoPilot} />
      )}
    </Box>
  );
}
